import styled from "styled-components";
import {
  Board,
  BoardHeader,
  BoardSearchResult,
  SubmitContainer
} from "../../../assets/GlobalStyles";
import {AdverInfo} from "../styles/common";
import React, {useState} from "react";

/**
 * Shared visual shell for the four campaign setup steps.
 *
 * The step components intentionally keep ownership of their business-specific
 * fields. This shell only supplies a consistent hierarchy, focus treatment and
 * responsive action area, so each step behaves the same without coupling their
 * form logic.
 */
export const CampaignStepForm = styled.form`
  --campaign-accent: #f5811f;
  --campaign-accent-soft: #fff6ed;
  --campaign-text: #242a31;
  --campaign-muted: #68727d;
  --campaign-line: #e6e9ed;
  --campaign-surface: #f5f7f9;

  display: grid;
  grid-template-columns: minmax(0, 1fr) 286px;
  gap: 22px;
  align-items: start;
  width: 100%;
  margin: 0 auto;
  color: var(--campaign-text);

  ${Board} {
    grid-column: 1;
    overflow: hidden;
    margin-bottom: 0;
    padding: 0;
    border: 1px solid var(--campaign-line);
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(31, 41, 55, 0.07);
  }

  ${AdverInfo} {
    grid-column: 1 / -1;
  }

  ${BoardHeader} {
    min-height: 76px;
    padding: 24px 30px;
    border-bottom: 1px solid var(--campaign-line);
    background: linear-gradient(135deg, #ffffff 0%, #fffaf5 100%);
    color: var(--campaign-text);
    font-size: 22px;
    font-weight: 750;
    letter-spacing: -0.02em;
  }

  ${BoardSearchResult} {
    margin: 0;
    padding: 28px 30px 10px;
    background: var(--campaign-surface);
  }

  ${SubmitContainer} {
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 0;
    padding: 14px;
    border-top: 1px solid var(--campaign-line);
    background: #fff;

    button {
      min-width: 100px;
      height: 44px;
      margin: 0;
      border-radius: 9px;
      font-size: 16px;
      font-weight: 700;
      transition: transform 140ms ease, box-shadow 140ms ease,
        background-color 140ms ease, border-color 140ms ease;
    }

    button:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 14px rgba(31, 41, 55, 0.12);
    }

    button:focus-visible {
      outline: 3px solid rgba(245, 129, 31, 0.24);
      outline-offset: 2px;
    }
  }

  input:not([type="checkbox"]):not([type="radio"]),
  textarea,
  select {
    min-height: 42px;
    border-radius: 8px;
    transition: border-color 140ms ease, box-shadow 140ms ease,
      background-color 140ms ease;
  }

  input:not([type="checkbox"]):not([type="radio"]):focus,
  textarea:focus,
  select:focus {
    border-color: var(--campaign-accent);
    box-shadow: 0 0 0 3px rgba(245, 129, 31, 0.13);
  }

  input:disabled,
  input[readonly],
  textarea:disabled,
  select:disabled {
    color: #7d8790;
  }

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 900px) {
    ${BoardHeader} {
      min-height: 64px;
      padding: 20px;
      font-size: 20px;
    }

    ${BoardSearchResult} {
      padding: 20px 16px 4px;
    }
  }

  @media (max-width: 640px) {
    ${Board} {
      border-radius: 12px;
    }

    ${SubmitContainer} {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      padding: 12px;

      button {
        width: 100%;
        min-width: 0;
      }
    }
  }
`;

export function CampaignStepSidebar({children, ...props}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
      <>
        <CampaignSidebarOpenButton
            type="button"
            onClick={() => setIsOpen(true)}
        >
          설정 정보
        </CampaignSidebarOpenButton>
        <CampaignSidebarOverlay
            type="button"
            $open={isOpen}
            aria-label="설정 정보 닫기"
            onClick={() => setIsOpen(false)}
        />
        <CampaignStepSidebarPanel {...props} $open={isOpen}>
          <CampaignSidebarCloseButton
              type="button"
              aria-label="설정 정보 닫기"
              onClick={() => setIsOpen(false)}
          >×</CampaignSidebarCloseButton>
          {children}
        </CampaignStepSidebarPanel>
      </>
  );
}

const CampaignStepSidebarPanel = styled.aside`
  grid-column: 2;
  position: sticky;
  top: 18px;
  overflow: hidden;
  border: 1px solid #e6e9ed;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(31, 41, 55, 0.08);

  > aside {
    position: static;
    overflow: visible;
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }

  @media (max-width: 1180px) {
    grid-column: 1;
    position: static;
  }

  @media (max-width: 900px) {
    position: fixed;
    top: 0;
    right: 0;
    z-index: 232;
    width: min(360px, 90vw);
    height: 100vh;
    max-height: 100vh;
    box-sizing: border-box;
    overflow-y: auto;
    padding-top: 48px;
    border-radius: 14px 0 0 14px;
    transform: translateX(${({$open}) => $open ? "0" : "105%"});
    transition: transform .22s ease;
    box-shadow: -18px 0 42px rgba(15, 23, 42, .2);
  }
`;

const CampaignSidebarOpenButton = styled.button`
  display: none;

  @media (max-width: 900px) {
    position: fixed;
    right: 16px;
    bottom: 18px;
    z-index: 205;
    display: inline-flex;
    min-width: 92px;
    height: 40px;
    align-items: center;
    justify-content: center;
    padding: 0 14px;
    border: 1px solid #f5811f;
    border-radius: 20px;
    background: #f5811f;
    box-shadow: 0 8px 22px rgba(245, 129, 31, .28);
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
  }
`;

const CampaignSidebarOverlay = styled.button`
  display: none;

  @media (max-width: 900px) {
    position: fixed;
    inset: 0;
    z-index: 231;
    display: ${({$open}) => $open ? "block" : "none"};
    width: 100%;
    height: 100%;
    padding: 0;
    border: 0;
    background: rgba(15, 23, 42, .42);
  }
`;

const CampaignSidebarCloseButton = styled.button`
  display: none;

  @media (max-width: 900px) {
    position: absolute;
    top: 10px;
    right: 12px;
    z-index: 2;
    display: inline-flex;
    width: 32px;
    height: 32px;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 1px solid #e5e8eb;
    border-radius: 8px;
    background: #fff;
    color: #59616a;
    font-size: 22px;
    cursor: pointer;
  }
`;
